import { type AppBus, type AppBusEvents } from '@/services/AppBus';
import { InvariantError } from '@/domain/errors';

// Events that must have a subscriber wired at boot. The bus drops a zero-handler publish silently,
// and the container is lazy, so a subscriber nobody eager-resolves never subscribes and the event
// vanishes with no error. This list turns that silent drop into a loud boot failure.
//
// THEME_CHANGED and URL_CHANGED are deliberately absent: no required subscriber today (theme applies via
// DomTheme; this app does not route), so listing them would fail the check for events fine with zero handlers.
export const BOOT_REQUIRED_SUBSCRIBERS: readonly (keyof AppBusEvents)[] = [
  'TOAST_REQUESTED', // ToastStore
  'KEY_DOWN', // ThemeStore (the `d` shortcut)
];

// Throws if any of `events` has no subscriber on the bus. Call once at boot, after the eager
// resolves, before render. The error names the unwired events so it points at the missing line.
export function assertBootSubscribers(
  bus: AppBus,
  events: readonly (keyof AppBusEvents)[] = BOOT_REQUIRED_SUBSCRIBERS,
): void {
  const missing = events.filter((event) => bus.handlerCount(event) === 0);
  if (missing.length > 0) {
    throw new InvariantError(
      `Boot subscriber check failed: no handler registered for ${missing.join(', ')}. ` +
        `A singleton that subscribes to the bus must be eager-resolved in main.tsx before render ` +
        `(see INVARIANTS rule 20).`,
    );
  }
}
